import { BlockStack, Box, Button, FormLayout, Modal, TextField, Text, Form, InlineGrid, InlineStack, Select } from '@shopify/polaris';
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import helpers from 'helpers';
import { lengthLessThan, lengthMoreThan, useField, useForm } from '@shopify/react-form';
import { TypedUser } from 'interface/user.model';
import DateTimeInput from 'components/dateTimeInput';
import __ from 'languages/index';
import { useMutateCreateCustomer } from 'queries/customer.query';
import Capacity from 'components/Capacity';
import { ENUM_SOCIAL_NAME } from 'queries/user_social_profile.query';

/**
 *   Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

function CustomerNew({
  onClose,
  show,
  defaultNumber = '',
  defaultEmail = '',
  defaultName = '',
  defaultSocialName = '',
  defaultSocialSlug = '',
}: {
  onClose: (userData?: TypedUser) => void;
  show: boolean;
  defaultNumber?: string;
  defaultEmail?: string;
  defaultName?: string;
  defaultSocialName?: string;
  defaultSocialSlug?: string;
}) {
  const { mutateAsync: createEntity } = useMutateCreateCustomer();

  const initialSocialProfiles = () => [{ social_name: defaultSocialName, social_slug: defaultSocialSlug }];
  const [socialProfiles, setSocialProfiles] = useState(initialSocialProfiles);
  const [socialProfileErrors, setSocialProfileErrors] = useState<Array<{ social_name?: string; social_slug?: string }>>([]);

  const history = useNavigate();

  const onCloseCallback = useCallback((args: any) => {
    onClose(args);
  }, []);

  /**
   * Save and close or save and moving to detail?
   */
  const saveAndClose = useRef(false);

  const useFields = {
    display_name: useField<string>({
      value: defaultName,
      validates: [lengthLessThan(50, 'Không được dài hơn 50 ký tự.'), lengthMoreThan(1, 'Tên phải dài hơn 1 ký tự.')],
    }),

    bio: useField<string>({
      value: '',
      validates: [lengthLessThan(250, 'No more than 250 characters.')],
    }),

    user_phonenumber: useField<string>({
      value: defaultNumber,
      validates: [
        (inputValue) => {
          if (inputValue && !helpers.isPhoneNumber(inputValue)) {
            return 'Số điện thoại không hợp lệ';
          }
        },
      ],
    }),

    user_birthday: useField<any>({
      value: '',
      validates: [],
    }),

    user_address: useField<string>({
      value: '',
      validates: [lengthLessThan(150, 'No more than 150 characters.')],
    }),

    user_title: useField<string>({
      value: '',
      validates: [],
    }),

    user_email: useField<string>({
      value: defaultEmail,
      validates: [
        (inputValue) => {
          if (inputValue)
            if (!helpers.isEmail(inputValue)) {
              return 'Your email is not valid!';
            }
        },
      ],
    }),
  };

  const { fields, submit, submitting, dirty, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      const normalizedSocialProfiles = socialProfiles.map((profile) => ({
        social_name: profile.social_name.trim(),
        social_slug: profile.social_slug.trim(),
      }));
      const errors = normalizedSocialProfiles.map((profile) => ({
        social_name: profile.social_slug && !profile.social_name ? 'Vui lòng chọn mạng xã hội.' : undefined,
        social_slug: profile.social_name && !profile.social_slug ? 'Vui lòng nhập tên hoặc đường dẫn profile.' : undefined,
      }));

      setSocialProfileErrors(errors);
      if (errors.some((error) => error.social_name || error.social_slug)) {
        return { status: 'fail', errors: [] };
      }

      try {
        // create new

        let userData = await createEntity({
          display_name: values.display_name,
          bio: values.bio,
          user_phonenumber: values.user_phonenumber,
          user_email: values.user_email,
          user_address: values.user_address,
          user_birthday: values.user_birthday || null,
          user_title: values.user_title,
          user_social_profile: normalizedSocialProfiles.filter((profile) => profile.social_name && profile.social_slug),
        });

        if (saveAndClose.current === true) {
          await helpers.sleep(1000);
          onCloseCallback(userData);
        } else {
          await helpers.sleep(1000);
          history('/customer/view/' + userData?.user_id);
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (!show) {
      helpers.resetForm(useFields);
      setSocialProfiles(initialSocialProfiles());
      setSocialProfileErrors([]);
    }
  }, [show]);

  const socialProfilesDirty = JSON.stringify(socialProfiles) !== JSON.stringify([{ social_name: defaultSocialName, social_slug: defaultSocialSlug }]);

  const updateSocialProfile = useCallback((index: number, field: 'social_name' | 'social_slug', value: string) => {
    setSocialProfiles((profiles) => profiles.map((profile, profileIndex) => (profileIndex === index ? { ...profile, [field]: value } : profile)));
    setSocialProfileErrors((errors) => errors.map((error, errorIndex) => (errorIndex === index ? { ...error, [field]: undefined } : error)));
  }, []);

  const addSocialProfile = useCallback(() => {
    setSocialProfiles((profiles) => [...profiles, { social_name: '', social_slug: '' }]);
  }, []);

  const removeSocialProfile = useCallback((index: number) => {
    setSocialProfiles((profiles) => profiles.filter((_, profileIndex) => profileIndex !== index));
    setSocialProfileErrors((errors) => errors.filter((_, errorIndex) => errorIndex !== index));
  }, []);

  const submitMode = useCallback((_mode: string) => {
    if (_mode === 'saveandclose') {
      saveAndClose.current = true;
    } else {
      saveAndClose.current = false;
    }
    submit();
  }, [submit]);

  return (
    <Modal
      open={show}
      onClose={() => onCloseCallback(null)}
      title={'Thêm khách hàng mới'}
      primaryAction={{
        content: 'Tạo & đóng',
        disabled: !dirty && !socialProfilesDirty,
        loading: submitting,
        onAction: () => {
          submitMode('saveandclose');
        },
      }}
      secondaryActions={[
        {
          content: 'Đóng',
          onAction: () => onCloseCallback(null),
        },
        {
          content: 'Tạo & Xem',
          disabled: (!dirty && !socialProfilesDirty) || submitting,
          onAction: () => {
            submitMode('any');
          },
        },
      ]}
    >
      <Modal.Section>
        <Capacity current_user_can={['manage_customers', 'edit_customers']} onFail={<>Bạn không có quyền truy cập.</>}>
          <Form onSubmit={submit}>
            <FormLayout>
              <TextField
                label="Tên hiển thị"
                autoComplete="off"
                error={fields.display_name.error}
                value={fields.display_name.value}
                onChange={(e) => fields.display_name.onChange(helpers.capitalizeWords(e))}
                connectedLeft={
                  <Select
                    value={useFields.user_title.value}
                    options={[
                      { value: '', label: 'Không set' },
                      { value: 'mr', label: 'Anh' },
                      { value: 'miss', label: 'Cô' },
                      { value: 'mrs', label: 'Chị' },

                      { value: 'madam', label: 'Quý bà' },
                      { value: 'sir', label: 'Quý ông' },
                    ]}
                    onChange={(e) => useFields.user_title.onChange(e)}
                    label={''}
                  />
                }
              />

              <InlineGrid gap="400" columns={2}>
                <TextField label="Số điện thoại" autoComplete="off" {...fields.user_phonenumber} type="tel" inputMode="numeric" pattern="[0-9]*" />

                <TextField label="Email" autoComplete="off" {...fields.user_email} />
              </InlineGrid>

              <DateTimeInput
                onDateChange={(dateObjectOrnull) => {
                  if (!dateObjectOrnull) {
                    fields.user_birthday.onChange(``);
                  } else {
                    let { day, month, year } = dateObjectOrnull;
                    fields.user_birthday.onChange(`${year}-${month}-${day}`);
                  }
                }}
                label={'Sinh nhật'}
                initialDate={fields.user_birthday.value}
                error={fields.user_birthday.error}
              />

              <TextField label="Số nhà, đường, thôn xóm" autoComplete="off" {...fields.user_address} />

              <TextField label="Ghi chú" autoComplete="off" multiline={2} {...fields.bio} />

              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">
                    Profile mạng xã hội
                  </Text>
                  <Button variant="plain" icon={PlusIcon} onClick={addSocialProfile}>
                    Thêm
                  </Button>
                </InlineStack>

                <BlockStack gap="300">
                  {socialProfiles.map((profile, index) => (
                    <Box key={index} background="bg-surface-secondary" padding="300" borderRadius="200">
                      <BlockStack gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">
                            Profile {index + 1}
                          </Text>
                          <Button
                            accessibilityLabel={`Xóa profile mạng xã hội ${index + 1}`}
                            variant="plain"
                            tone="critical"
                            icon={DeleteIcon}
                            onClick={() => removeSocialProfile(index)}
                          >
                            Xóa
                          </Button>
                        </InlineStack>
                        <InlineGrid columns={{ xs: 1, sm: ['oneThird', 'twoThirds'] }} gap="300">
                          <Select
                            label="Mạng xã hội"
                            options={[
                              { label: 'Chọn mạng xã hội', value: '' },
                              ...Object.values(ENUM_SOCIAL_NAME).map((social) => ({
                                label: social.charAt(0).toUpperCase() + social.slice(1),
                                value: social,
                              })),
                            ]}
                            value={profile.social_name}
                            error={socialProfileErrors[index]?.social_name}
                            onChange={(value) => updateSocialProfile(index, 'social_name', value)}
                          />
                          <TextField
                            label="Tên hoặc đường dẫn profile"
                            placeholder="Ví dụ: user3223 hoặc nguyenvana ..."
                            autoComplete="off"
                            value={profile.social_slug}
                            error={socialProfileErrors[index]?.social_slug}
                            onChange={(value) => updateSocialProfile(index, 'social_slug', value)}
                          />
                        </InlineGrid>
                      </BlockStack>
                    </Box>
                  ))}
                </BlockStack>
              </BlockStack>
            </FormLayout>
          </Form>
          <br />
          <InlineStack gap={'500'}>
            <Text as="p" variant="bodyMd">
              Bạn sẽ thêm được các thông tin khác trong giao diện chỉnh sửa toàn diện
            </Text>
          </InlineStack>
        </Capacity>
      </Modal.Section>
    </Modal>
  );
}

export default memo(CustomerNew);

